import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable } from 'rxjs/Observable';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { startWith, map } from 'rxjs/operators';
import { MatTableDataSource } from '@angular/material';
import { ActosService } from '../../../../services/actos/actos.service';
import { ShiftService } from '../../../../services/shifts/shift.service';
import { fuseAnimations } from '../../../../../core/animations';
import { TranslationService } from '../../../../services/translation/translation.service';
import { SweetAlertService } from '../../../../services/sweet-alert/sweet-alert.service';
import { BlockUI, NgBlockUI } from 'ng-block-ui';
import { SchoolYearService } from '../../../../services/schoool-year/school-year.service';
import { EmployeeService } from '../../../../services/employee/employee.service';

@Component({
    selector: 'fuse-actos',
    templateUrl: './actos.component.html',
    styleUrls: ['./actos.component.scss'],
    animations: fuseAnimations
})
export class ActosComponent implements OnInit, OnDestroy {
    @BlockUI('tracker') blockUI: NgBlockUI;

    acts = [];

    shifts = [];
    dataSource = new MatTableDataSource<any>([]);
    displayedColumns = ['Acto', 'DiaOriginal', 'CommemorationDate', 'Dia'];

    // Filters
    filterByDay = false;
    uniqueByShift = false;

    // Controls for autocomplete
    // We'll have one control per row and per shift column
    controls: Map<string, FormControl> = new Map();
    filteredProfessors: Map<string, Observable<any[]>> = new Map();
    allProfessorsByShift: Map<number, any[]> = new Map();
    selectedProfessors: Map<string, any[]> = new Map(); // Key: actId-shiftId

    // Sidebar Properties
    sidebarTeachers: any[] = [];
    filteredSidebarTeachers: Observable<any[]>;
    sidebarTeacherControl = new FormControl('');
    selectedSidebarShift: any;
    selectedSidebarTeacher: any;
    sidebarFilterDaysCount: number = null;
    sidebarFilterSpecificDay: number = null;
    currentSchoolYearId: number;
    scheduleShiftsData: any[] = []; // List of ShiftReportData
    daysOfWeek = ['LUNES', 'MARTES', 'MIERCOLES', 'JUEVES', 'VIERNES'];
    dayIds = [2, 3, 4, 5, 6];
    availableDayCounts = [1, 2, 3, 4, 5];

    constructor(
        private actosService: ActosService,
        private shiftService: ShiftService,
        private translationService: TranslationService,
        private sweetAlertService: SweetAlertService,
        private schoolYearService: SchoolYearService,
        private employeeService: EmployeeService
    ) { }

    ngOnInit() {
        this.blockUI.start();

        this.filteredSidebarTeachers = this.sidebarTeacherControl.valueChanges.pipe(
            startWith(''),
            map(value => typeof value === 'string' ? value : (value as any).FullName),
            map(name => this._filterSidebar(name || ''))
        );

        // Load school year, shifts, and acts simultaneously
        forkJoin([
            this.schoolYearService.getAll(),
            this.shiftService.getAll(),
            this.actosService.getAll()
        ]).subscribe(([years, shifts, actsRes]) => {
            const activeYear = years.find(y => y.SchoolYearState.Id === 2);
            if (activeYear) {
                this.currentSchoolYearId = activeYear.Id;
            }

            this.shifts = shifts;
            if (actsRes.ResultCode === 1) {
                this.acts = actsRes.Result;
            }

            const columns = ['Acto', 'DiaOriginal', 'CommemorationDate', 'Dia'];
            this.shifts.forEach(s => {
                columns.push('shift-' + s.Id);
                if (this.currentSchoolYearId) {
                    this.loadProfessorsForShift(s.Id, this.currentSchoolYearId);
                }
            });
            this.initializeDataSource();
            this.displayedColumns = columns;
            this.blockUI.stop();
        });
    }

    initializeDataSource() {
        this.dataSource.data = this.acts.map(act => {
            const rawDate = act.CommemorationDate || act.date;
            const rawDiaOriginal = act.DiaOriginal;
            const actId = act.Id || act.id;
            const actName = act.Name || act.name || '';

            let date: Date;
            if (rawDate && rawDate.includes('T')) {
                date = new Date(rawDate);
            } else if (rawDate) {
                const dateParts = rawDate.split('/');
                date = new Date(+dateParts[2], +dateParts[1] - 1, +dateParts[0]);
            } else {
                date = new Date();
            }

            let diaOriginalDate: Date;
            if (rawDiaOriginal && rawDiaOriginal.includes('T')) {
                diaOriginalDate = new Date(rawDiaOriginal);
            } else if (rawDiaOriginal) {
                const dateParts = rawDiaOriginal.split('/');
                diaOriginalDate = new Date(+dateParts[2], +dateParts[1] - 1, +dateParts[0]);
            } else {
                diaOriginalDate = date;
            }

            const dayName = this.getDayNameSpanish(date.getDay());
            const dayId = this.getDayId(date.getDay());

            const row = {
                ...act,
                id: actId,
                name: actName,
                diaOriginalFormatted: this.formatDate(diaOriginalDate),
                commemorationDateFormatted: this.formatDate(date),
                dayName: dayName,
                dayId: dayId
            };

            this.shifts.forEach(shift => {
                const key = `${actId}-${shift.Id}`;
                const control = new FormControl('');
                this.controls.set(key, control);

                // Initialize selected professors from backend data
                const savedAssignments = act.ActoShiftProfessors || [];
                const matchedAssignments = savedAssignments
                    .filter(asp => asp.ShiftId === shift.Id)
                    .map(asp => asp.Employee);

                this.selectedProfessors.set(key, matchedAssignments);

                this.filteredProfessors.set(key, control.valueChanges.pipe(
                    startWith(''),
                    map(value => typeof value === 'string' ? value : (value as any).FullName),
                    map(name => this._filter(name, actId, shift.Id, dayId))
                ));
            });

            return row;
        });
    }

    getAvailableCount(shiftId: number, dayId: number): number {
        const list = this.allProfessorsByShift.get(shiftId) || [];
        return list.filter(p => p.WorkingDayIds && p.WorkingDayIds.some(id => +id === +dayId)).length;
    }

    onFocus(actId: number, shiftId: number) {
        const key = `${actId}-${shiftId}`;
        const control = this.controls.get(key);
        if (control) {
            // Trigger value changes to show the list if it's empty
            control.setValue(control.value || '');
        }
    }

    loadProfessorsForShift(shiftId: number, schoolYearId: number) {
        this.actosService.getProfessorsByShiftAndDay(shiftId, schoolYearId).subscribe(res => {
            if (res.ResultCode === 1) {
                this.allProfessorsByShift.set(shiftId, res.Result);
            }
        });
    }

    onSidebarShiftChange(shiftId: number) {
        this.selectedSidebarTeacher = null;
        this.sidebarFilterDaysCount = null;
        this.sidebarFilterSpecificDay = null;
        this.sidebarTeacherControl.setValue('');
        this.scheduleShiftsData = [];
        if (this.currentSchoolYearId) {
            this.loadProfessorsForShift(shiftId, this.currentSchoolYearId);
        }
        this.sidebarTeachers = this.allProfessorsByShift.get(shiftId) || [];
        // Trigger filter update
        this.sidebarTeacherControl.setValue('');
    }

    onSidebarFilterChange() {
        // If current specific day filter has 0 count with the new count filter, reset it
        if (this.sidebarFilterSpecificDay !== null && this.getSidebarDayCount(this.sidebarFilterSpecificDay) === 0) {
            this.sidebarFilterSpecificDay = null;
        }
        // Trigger autocomplete filter refresh
        this.sidebarTeacherControl.setValue(this.sidebarTeacherControl.value || '');
    }

    getSidebarDayCount(dayId: number): number {
        if (!this.sidebarTeachers) return 0;
        let list = this.sidebarTeachers;
        if (this.sidebarFilterDaysCount !== null && this.sidebarFilterDaysCount !== undefined) {
            list = list.filter(p => (p.WorkingDayIds ? p.WorkingDayIds.length : 0) === +this.sidebarFilterDaysCount);
        }
        return list.filter(p => p.WorkingDayIds && p.WorkingDayIds.some(id => +id === +dayId)).length;
    }

    displayFn(user?: any): string | undefined {
        return user ? user.FullName : undefined;
    }

    private _filterSidebar(name: string): any[] {
        const filterValue = (name || '').toLowerCase();
        let list = this.sidebarTeachers;

        // Apply "Count of days" filter
        if (this.sidebarFilterDaysCount !== null && this.sidebarFilterDaysCount !== undefined) {
            list = list.filter(p => (p.WorkingDayIds ? p.WorkingDayIds.length : 0) === +this.sidebarFilterDaysCount);
        }

        // Apply "Specific day" filter (non-exclusive)
        if (this.sidebarFilterSpecificDay !== null && this.sidebarFilterSpecificDay !== undefined) {
            list = list.filter(p => {
                if (!p.WorkingDayIds) return false;
                return p.WorkingDayIds.some(id => +id === +this.sidebarFilterSpecificDay);
            });
        }

        return list.filter(option => (option.FullName || '').toLowerCase().includes(filterValue));
    }

    onSidebarTeacherSelected(event: any) {
        const teacher = event.option.value;
        this.selectedSidebarTeacher = teacher.Id;
        this.onSidebarTeacherChange(teacher.Id);
    }

    onSidebarTeacherChange(teacherId: string) {
        if (!teacherId || !this.currentSchoolYearId) return;

        this.blockUI.start('Cargando horario...');
        this.actosService.getTeacherScheduleData(teacherId, this.currentSchoolYearId).subscribe(res => {
            if (res.ResultCode === 1) {
                this.scheduleShiftsData = res.Result;
            }
            this.blockUI.stop();
        }, error => {
            this.blockUI.stop();
            this.sweetAlertService.error('Error', 'No se pudo cargar el horario del docente.');
        });
    }

    getScheduleItems(shiftData: any, moduleId: number, dayId: number): any[] {
        const items = [];
        const slotKey = moduleId * 10 + dayId;

        shiftData.ScheduleData.forEach(data => {
            if (data.EventRepeatIds.includes(slotKey)) {
                items.push(data);
            }
        });

        return items;
    }

    getPastelColor(divisionId: number, subjectId: number): string {
        const palette = [
            '#FFCCCC', '#CCFFCC', '#CCE5FF', '#F5F5DC', '#E5CCFF',
            '#FFE5CC', '#CCFFFF', '#FFDAB9', '#F0E68C', '#B0E0E6'
        ];
        const hash = (divisionId * 31 + subjectId) % palette.length;
        return palette[hash];
    }

    getDayNameSpanish(day: number) {
        const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
        return days[day];
    }

    // Map JS getDay() to DB RepeatId (assuming 1=Sunday, 2=Monday... like in SchoolScheduleController)
    getDayId(day: number) {
        return day + 1;
    }

    getDayColor(dayId: number): string {
        const colors = {
            1: '#ECECEC', // Domingo
            2: '#E3F2FD', // Lunes (Light Blue)
            3: '#F1F8E9', // Martes (Light Green)
            4: '#FFF3E0', // Miércoles (Light Orange)
            5: '#F3E5F5', // Jueves (Light Purple)
            6: '#FFFDE7', // Viernes (Light Yellow)
            7: '#F5F5F5'  // Sábado
        };
        return colors[dayId] || 'transparent';
    }

    formatDate(date: Date): string {
        const d = date.getDate();
        const m = date.getMonth() + 1;
        const y = date.getFullYear();
        return (d < 10 ? '0' + d : d) + '/' + (m < 10 ? '0' + m : m) + '/' + y;
    }

    isDuplicateTeacher(professorId: number, shiftId: number): boolean {
        let count = 0;
        this.acts.forEach(act => {
            const actId = act.Id || act.id;
            const key = `${actId}-${shiftId}`;
            const selected = this.selectedProfessors.get(key) || [];
            if (selected.find(p => p.Id === professorId || (p as any).id === professorId)) {
                count++;
            }
        });
        return count > 1;
    }

    private _filter(name: string, actId: number, shiftId: number, dayId: number): any[] {
        let list = this.allProfessorsByShift.get(shiftId) || [];

        // Apply Global Filter 1: By Day
        if (this.filterByDay) {
            list = list.filter(p => {
                if (!p.WorkingDayIds) return false;
                // Ensure comparison works regardless of type (string/number)
                return p.WorkingDayIds.some(id => +id === +dayId);
            });
        }

        // Apply Global Filter 2: Uniqueness by shift
        if (this.uniqueByShift) {
            const alreadySelectedInOtherActs = [];
            this.selectedProfessors.forEach((profs, key) => {
                const [aId, sId] = key.split('-').map(Number);
                if (sId === shiftId && aId !== actId) {
                    profs.forEach(p => alreadySelectedInOtherActs.push(p.Id));
                }
            });
            list = list.filter(p => !alreadySelectedInOtherActs.includes(p.Id));
        }

        if (name) {
            const filterValue = name.toLowerCase();
            return list.filter(p => p.FullName.toLowerCase().includes(filterValue));
        }
        return list;
    }

    refreshAllFilters() {
        this.controls.forEach(control => {
            control.setValue(control.value);
        });
    }

    onProfessorSelected(event: any, actId: number, shiftId: number) {
        const professor = event.option.value;
        const key = `${actId}-${shiftId}`;
        const selected = this.selectedProfessors.get(key);
        if (!selected.find(p => p.Id === professor.Id)) {
            selected.push(professor);
        }
        this.controls.get(key).setValue('');
    }

    removeProfessor(professor: any, actId: number, shiftId: number) {
        const key = `${actId}-${shiftId}`;
        const selected = this.selectedProfessors.get(key);
        const index = selected.indexOf(professor);
        if (index >= 0) {
            selected.splice(index, 1);
        }
        // Force re-trigger of filtering for uniqueness
        this.controls.get(key).setValue(this.controls.get(key).value);
    }

    save() {
        this.blockUI.start('Guardando...');

        const payload = [];
        this.selectedProfessors.forEach((professors, key) => {
            const [actId, shiftId] = key.split('-').map(Number);
            payload.push({
                ActoId: actId,
                ShiftId: shiftId,
                EmployeeIds: professors.map(p => p.Id || (p as any).id)
            });
        });

        this.actosService.saveAllProfessors(payload).subscribe(res => {
            this.blockUI.stop();
            if (res.ResultCode === 1) {
                this.sweetAlertService.success('Éxito', res.ResultMsg, null);
            } else {
                this.sweetAlertService.error('Error', res.ResultMsg || 'Ha ocurrido un error al guardar.', null);
            }
        }, err => {
            this.blockUI.stop();
            this.sweetAlertService.error('Error', 'Ha ocurrido un error de red al guardar.', null);
        });
    }

    download() {
        if (!this.currentSchoolYearId) {
            this.sweetAlertService.error('Error', 'No hay un ciclo lectivo seleccionado.', null);
            return;
        }

        this.blockUI.start('Generando PDF...');
        this.actosService.downloadReport(this.currentSchoolYearId).subscribe(blob => {
            this.blockUI.stop();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Actos_Escolares_${new Date().getFullYear()}.pdf`;
            link.click();
            window.URL.revokeObjectURL(url);
        }, err => {
            this.blockUI.stop();
            this.sweetAlertService.error('Error', 'Ha ocurrido un error al generar el PDF.', null);
        });
    }

    ngOnDestroy() { }
}



// WEBPACK FOOTER //
// ./src/app/main/content/pages/management/actos/actos.component.ts